import { useEvento } from "@/store/evento/evento";
import { useEventoContext } from "../../contexts/eventos";
import { EntradaSeleccionada } from "@/schemas/checkout.schema";
import { useMemo } from "react";
import { StockDisponibleResponse } from "@/services/api/new.funciones.service";

const MAX_ENTRIES_PER_PURCHASE = 4;

type Props = {
    stockReservado?: StockDisponibleResponse['entradas'];
}

/**
 * Selector interactivo de entradas para eventos sin mapa.
 * Incluye botones +/- para agregar/quitar entradas.
 */
export function SelectorEntradas({ stockReservado = [] }: Props) {

    const { state: funcion } = useEvento.use.funcion();
    const { state: entradas, removeAsiento, setAsiento } = useEvento.use.entradas();
    const { tipos } = useEventoContext();

    // Entradas de la función indexadas por tipoEntradaId
    const entradasFuncionByTipo = useMemo(() =>
        funcion!.entradas.reduce((prev, actual) => ({
            ...prev,
            [actual.tipoEntradaId]: { ...actual }
        }), {} as Record<string, { tipoEntradaId: string; precio: number; capacidad: number }>)
        , [funcion]);

    // Entradas seleccionadas indexadas por tipoEntradaId
    const entradasByTipo = useMemo(() =>
        entradas.reduce((prev, actual) => ({
            ...prev,
            [actual.tipoEntradaId]: { ...actual }
        }), {} as Record<string, EntradaSeleccionada>)
        , [entradas]);

    // Total de entradas seleccionadas
    const totalSeleccionadas = useMemo(() =>
        entradas.reduce((acc, entrada) => acc + (entrada.asientos?.length || 0), 0)
        , [entradas]);

    const limiteAlcanzado = totalSeleccionadas >= MAX_ENTRIES_PER_PURCHASE;

    const handleAdd = (tipoId: string) => {
        if (limiteAlcanzado) return;

        const stockInfo = stockReservado.find(s => s.tipoEntradaId === tipoId);
        const seleccionadas = entradasByTipo[tipoId]?.asientos?.length ?? 0;
        const disponible = (stockInfo?.disponible ?? 0);

        // Verificar que hay stock disponible
        if (disponible <= 0) return;

        setAsiento({
            tipoEntradaId: tipoId,
            seatId: null as any,
            mesaId: '' as any,
            selected: true
        })
    }

    const handleRemove = (tipoId: string) => {
        const seleccionadas = entradasByTipo[tipoId]?.asientos?.length ?? 0;

        if (seleccionadas <= 0) return;

        removeAsiento({
            tipoEntradaId: tipoId,
            seatId: null as any,
            mesaId: '' as any,
            selected: false
        })
    }

    return (
        <div className="text-white">
            {tipos.map((tipo) => {
                const actualColor = tipo.color || '#7c3aed';
                const entradaFuncion = entradasFuncionByTipo[tipo.id];

                const stockInfo = stockReservado.find(s => s.tipoEntradaId === tipo.id);
                const seleccionadas = entradasByTipo[tipo.id]?.asientos?.length ?? 0;
                const disponibles = Math.max(0, (stockInfo?.disponible ?? 0) - seleccionadas);
                const agotado = disponibles <= 0;

                return (
                    <div key={tipo.id} className="py-3">
                        {/* Fila principal: nombre */}
                        <div className="flex items-center gap-2">
                            <span
                                className="w-2.5 h-2.5 rounded-full"
                                style={{ backgroundColor: actualColor }}
                            />
                            <span className="font-semibold">{tipo.nombre}</span>
                        </div>

                        {/* Segunda fila: precio, contador, subtotal */}
                        <div className="grid grid-cols-[55px_1fr_85px] xl:grid-cols-[80px_1fr_100px] items-center gap-2 lg:gap-4 mt-1 pl-[18px]">
                            {/* Precio */}
                            <span className="text-xs lg:text-base text-white/60">
                                ${entradasFuncionByTipo[tipo.id]?.precio ?? 0}
                            </span>

                            {/* Contador con botones - centrado */}
                            <div className="flex items-center justify-center gap-2">
                                {!agotado && <button
                                    className="w-8 h-8 text-base font-bold rounded-lg disabled:opacity-40 hover:opacity-80 flex items-center justify-center shadow-sm"
                                    style={{ backgroundColor: actualColor }}
                                    onClick={() => handleRemove(tipo.id)}
                                    disabled={seleccionadas <= 0}
                                >
                                    -
                                </button>}

                                {agotado ? (
                                    <span className="text-sm font-semibold text-red-400 uppercase tracking-wide">
                                        Agotado
                                    </span>
                                ) : (
                                    <span className="text-lg lg:text-xl font-bold tabular-nums text-white">
                                        {seleccionadas}
                                    </span>
                                )}

                                {!agotado && <button
                                    className="w-8 h-8 text-base font-bold rounded-lg disabled:opacity-40 hover:opacity-80 flex items-center justify-center shadow-sm"
                                    style={{ backgroundColor: actualColor }}
                                    onClick={() => handleAdd(tipo.id)}
                                    disabled={agotado || limiteAlcanzado}
                                >
                                    +
                                </button>}
                            </div>

                            {/* Subtotal - alineado a la derecha */}
                            <span className="text-base lg:text-xl font-bold tabular-nums text-right">
                                ${(entradasFuncionByTipo[tipo.id]?.precio ?? 0) * seleccionadas}
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
